import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api from "../api";
import SEO from "../components/SEO";
import { useAuth } from "../contexts/AuthContext";

/* ── Thinking Progress (inline) ─────────────────────── */
function ThinkingStep({ steps, loading }) {
  const [visible, setVisible] = useState([]);
  const [elapsed, setElapsed] = useState(0);
  const [collapsed, setCollapsed] = useState(!loading);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!loading) { setElapsed(0); return; }
    const start = Date.now();
    const iv = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(iv);
  }, [loading]);

  useEffect(() => {
    if (!loading || !steps?.length) { if (!loading && steps?.length) setVisible(steps); else setVisible([]); return; }
    setVisible([steps[0]]);
    let i = 1;
    timerRef.current = setInterval(() => {
      if (i < steps.length) { setVisible(p => [...p, steps[i]]); i++; }
      else clearInterval(timerRef.current);
    }, 2200);
    return () => clearInterval(timerRef.current);
  }, [loading, steps]);

  const isCompleted = !loading && visible.length > 0;
  if (!loading && visible.length === 0) return null;

  return (
    <div className="rounded-xl border p-4 mb-4" style={{ background: 'linear-gradient(135deg, rgba(99,102,241,0.06), rgba(139,92,246,0.06))', borderColor: 'rgba(99,102,241,0.15)' }}>
      <div onClick={() => setCollapsed(c => !c)} className="flex items-center gap-2 cursor-pointer select-none" style={{ marginBottom: collapsed ? 0 : 6 }}>
        <span className={`text-sm ${!isCompleted ? 'animate-spin' : ''}`} style={{ background: 'linear-gradient(135deg,#9333ea,#6366f1)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>✦</span>
        <span className="text-xs font-bold" style={{ background: 'linear-gradient(90deg,#9333ea,#6366f1)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          {isCompleted ? '생각한 과정' : '생각하는 중...'}
        </span>
        {!isCompleted && elapsed > 0 && <span className="text-[10px] text-gray-400 ml-auto">{elapsed}초</span>}
        <span className={`text-[10px] text-gray-400 transition-transform ${collapsed ? '-rotate-90' : ''} ${(!isCompleted && elapsed > 0) ? 'ml-1' : 'ml-auto'}`}>▼</span>
      </div>
      {!collapsed && <div className="pl-1">
        {visible.map((s, i) => {
          const isCurrent = !isCompleted && i === visible.length - 1;
          return <div key={i} className="text-[11.5px] leading-7 flex items-center gap-2" style={{ color: isCurrent ? '#9333ea' : '#6b7280' }}>
            <span className="text-[10px] w-3.5 text-center">{isCurrent ? '·' : '✓'}</span>
            <span style={{ fontWeight: isCurrent ? 600 : 400 }}>{s}</span>
          </div>;
        })}
      </div>}
    </div>
  );
}

/* ── SVG Icon Library ─────────────────────────────────── */
const sv = (d, s = 16) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{display:'inline-block',verticalAlign:'middle',flexShrink:0}}><path d={d}/></svg>;
const IC = {
  doc:      sv("M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8zM14 2v6h6M16 13H8M16 17H8M10 9H8"),
  globe:    sv("M12 2a10 10 0 100 20 10 10 0 000-20zM2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10A15.3 15.3 0 0112 2z"),
  pen:      sv("M17 3l4 4L7 21H3v-4zM14 6l4 4"),
  image:    sv("M5 3h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2zM8.5 10a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM21 15l-5-5L5 21"),
  coin:     sv("M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"),
  chat:     sv("M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"),
  clip:     sv("M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2M9 2h6a1 1 0 011 1v2a1 1 0 01-1 1H9a1 1 0 01-1-1V3a1 1 0 011-1z"),
  key:      sv("M21 2l-2 2-3.5 3.5M7.5 15.5a5.5 5.5 0 110-11 5.5 5.5 0 010 11zM11 12l5-5M16 7l2 2"),
  check:    sv("M20 6L9 17l-5-5"),
  swap:     sv("M7 16l-4-4 4-4M17 8l4 4-4 4M3 12h18"),
  bulb:     sv("M9 18h6M10 22h4M12 2a7 7 0 00-4 12.73V17h8v-2.27A7 7 0 0012 2z"),
  mail:     sv("M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2zM22 6l-10 7L2 6"),
  chart:    sv("M18 20V10M12 20V4M6 20v-6"),
  file:     sv("M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"),
  mega:     sv("M3 11l18-5v12L3 13v-2z"),
  letter:   sv("M22 12h-6l-2 3h-4l-2-3H2"),
  calendar: sv("M19 4H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V6a2 2 0 00-2-2zM3 10h18M8 2v4M16 2v4"),
  user:     sv("M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 3a4 4 0 100 8 4 4 0 000-8z"),
  camera:   sv("M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2zM12 13a4 4 0 100-8 4 4 0 000 8z"),
  search:   sv("M11 19a8 8 0 100-16 8 8 0 000 16zM21 21l-4.35-4.35"),
  target:   sv("M12 22a10 10 0 100-20 10 10 0 000 20zM12 18a6 6 0 100-12 6 6 0 000 12zM12 14a2 2 0 100-4 2 2 0 000 4z"),
  scope:    sv("M12 22a10 10 0 100-20 10 10 0 000 20zM2 12h4M18 12h4M12 2v4M12 18v4"),
  warn:     sv("M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0zM12 9v4M12 17h.01"),
  printer:  sv("M6 9V2h12v7M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2M6 14h12v8H6z"),
  palette:  sv("M12 2a10 10 0 000 20c.73 0 1.4-.4 1.73-1.07.23-.47.1-1.01-.2-1.4-.43-.55-.64-1.22-.64-1.93 0-1.66 1.34-3 3-3h1.77A5.77 5.77 0 0022 9C22 5.13 17.52 2 12 2z"),
  grid:     sv("M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z"),
  copy:     sv("M20 9h-9a2 2 0 00-2 2v9a2 2 0 002 2h9a2 2 0 002-2v-9a2 2 0 00-2-2zM5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"),
  bag:      sv("M6 2l.01 2L6 8c0 2.21 1.79 4 4 4s4-1.79 4-4l-.01-4L14 2M6 2h8"),
  scale:    sv("M16 3l5 5M2 12.5l3-3 3 3M16.5 17.5l3-3 3 3M8.5 8L2 12.5M21 12.5L14.5 8M2 12.5V20M21 12.5V20"),
};

const TABS = [
  { id: "summarize", label: "문서 요약", icon: IC.doc, desc: "문서를 AI가 핵심만 요약" },
  { id: "translate", label: "AI 번역", icon: IC.globe, desc: "비즈니스 맞춤 다국어 번역" },
  { id: "write", label: "문서 작성", icon: IC.pen, desc: "비즈니스 문서 자동 생성" },
  { id: "image", label: "이미지 분석", icon: IC.image, desc: "OCR · 인쇄품질 · 이미지 분석" },
  { id: "optimizer", label: "인쇄비용 최적화", icon: IC.coin, desc: "인쇄 환경 분석 + 복합기 추천" },
  { id: "ask", label: "AI 질문", icon: IC.chat, desc: "무엇이든 AI에게 질문" },
];

/* ── shared styling ──────────────────────────────────── */
const card = "bg-white rounded-2xl border border-gray-200/80 shadow-sm p-6";
const btn = "px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-40";
const inputCls = "w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent";
const selectCls = "rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400";
const labelCls = "block text-xs font-semibold text-gray-500 mb-1.5";

/* ── Summarize Tab ───────────────────────────────────── */
function SummarizeTab() {
  const [text, setText] = useState("");
  const [style, setStyle] = useState("business");
  const [lang, setLang] = useState("ko");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!text.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/api/ai-tools/summarize", { text, style, lang });
      setResult(data);
    } catch (e) {
      setResult({ error: e.response?.data?.error || "오류 발생" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-3">
        <div>
          <label className={labelCls}>요약 스타일</label>
          <select value={style} onChange={e => setStyle(e.target.value)} className={selectCls}>
            <option value="business">비즈니스</option>
            <option value="detailed">상세</option>
            <option value="bullet">불릿 포인트</option>
            <option value="oneline">한 줄 요약</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>응답 언어</label>
          <select value={lang} onChange={e => setLang(e.target.value)} className={selectCls}>
            <option value="ko">한국어</option>
            <option value="en">English</option>
            <option value="ja">日本語</option>
            <option value="zh">中文</option>
          </select>
        </div>
      </div>

      <textarea
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder="요약할 텍스트를 붙여넣으세요 (최대 30,000자)..."
        rows={8}
        className={inputCls + " resize-y"}
      />
      <div className="flex items-center gap-3">
        <button onClick={run} disabled={loading || !text.trim()} className={`${btn} bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700`}>
          {loading ? "분석 중..." : "요약하기"}
        </button>
        <span className="text-xs text-gray-400">{text.length.toLocaleString()}자</span>
      </div>

      {loading && <ThinkingStep loading={true} steps={['문서 구조 파악 중...', '핵심 내용 추출 중...', '요약문 생성 중...']} />}

      {result && !result.error && (
        <div className={card + " space-y-4"}>
          {result.category && (
            <span className="inline-block px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-xs font-semibold">{result.category}</span>
          )}
          <div>
            <h4 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-1.5">{IC.clip} 요약</h4>
            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{result.summary}</p>
          </div>
          {result.key_points?.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-1.5">{IC.key} 핵심 포인트</h4>
              <ul className="space-y-1">
                {result.key_points.map((p, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="text-blue-500 mt-0.5">•</span>{p}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {result.action_items?.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-1.5">{IC.check} 실행 항목</h4>
              <ul className="space-y-1">
                {result.action_items.map((a, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="text-green-500 mt-0.5">✓</span>{a}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      {result?.error && <p className="text-red-500 text-sm">{result.error}</p>}
    </div>
  );
}

/* ── Translate Tab ───────────────────────────────────── */
function TranslateTab() {
  const [text, setText] = useState("");
  const [source, setSource] = useState("auto");
  const [target, setTarget] = useState("en");
  const [tone, setTone] = useState("formal");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const LANGS = [
    { code: "auto", label: "자동감지" }, { code: "ko", label: "한국어" },
    { code: "en", label: "English" }, { code: "ja", label: "日本語" },
    { code: "zh", label: "中文" }, { code: "de", label: "Deutsch" },
    { code: "ru", label: "Русский" }, { code: "es", label: "Español" },
    { code: "fr", label: "Français" },
  ];

  const run = async () => {
    if (!text.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/api/ai-tools/translate", { text, source, target, tone });
      setResult(data);
    } catch (e) {
      setResult({ error: e.response?.data?.error || "오류 발생" });
    } finally {
      setLoading(false);
    }
  };

  const swap = () => {
    if (source === "auto" || !result) return;
    const tmp = source;
    setSource(target);
    setTarget(tmp);
    setText(result.translated || "");
    setResult(null);
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className={labelCls}>원문 언어</label>
          <select value={source} onChange={e => setSource(e.target.value)} className={selectCls}>
            {LANGS.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
          </select>
        </div>
        <button onClick={swap} className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 transition" title="언어 교환">{IC.swap}</button>
        <div>
          <label className={labelCls}>번역 대상</label>
          <select value={target} onChange={e => setTarget(e.target.value)} className={selectCls}>
            {LANGS.filter(l => l.code !== "auto").map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>어조</label>
          <select value={tone} onChange={e => setTone(e.target.value)} className={selectCls}>
            <option value="formal">격식체</option>
            <option value="casual">일상체</option>
            <option value="polite">정중체</option>
            <option value="technical">기술 문서</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder="번역할 텍스트를 입력하세요..."
          rows={7}
          className={inputCls + " resize-y"}
        />
        <div className={`${inputCls} resize-y min-h-[180px] overflow-auto whitespace-pre-wrap ${result?.translated ? 'text-gray-800' : 'text-gray-400'}`}>
          {loading ? "번역 중..." : result?.translated || "번역 결과가 여기에 표시됩니다"}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button onClick={run} disabled={loading || !text.trim()} className={`${btn} bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700`}>
          {loading ? "번역 중..." : "번역하기"}
        </button>
        {result?.detected_language && (
          <span className="text-xs text-gray-400">감지 언어: {result.detected_language}</span>
        )}
      </div>

      {loading && <ThinkingStep loading={true} steps={['언어 감지 중...', '전문 용어 분석 중...', '번역문 생성 중...']} />}

      {result?.notes?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <h4 className="text-xs font-semibold text-amber-700 mb-2 flex items-center gap-1.5">{IC.bulb} 번역 참고</h4>
          <ul className="space-y-1">
            {result.notes.map((n, i) => <li key={i} className="text-xs text-amber-800">{n}</li>)}
          </ul>
        </div>
      )}
      {result?.error && <p className="text-red-500 text-sm">{result.error}</p>}
    </div>
  );
}

/* ── Write Tab ───────────────────────────────────────── */
function WriteTab() {
  const [prompt, setPrompt] = useState("");
  const [docType, setDocType] = useState("email");
  const [lang, setLang] = useState("ko");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const DOC_TYPES = [
    { value: "email", label: "비즈니스 이메일" },
    { value: "proposal", label: "제안서/기획서" },
    { value: "report", label: "보고서" },
    { value: "contract", label: "계약서 초안" },
    { value: "notice", label: "공지/안내문" },
    { value: "letter", label: "공식 서신" },
    { value: "meeting", label: "회의록" },
    { value: "resume", label: "이력서/자소서" },
  ];

  const run = async () => {
    if (!prompt.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/api/ai-tools/write", { prompt, type: docType, lang });
      setResult(data);
    } catch (e) {
      setResult({ error: e.response?.data?.error || "오류 발생" });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (result?.content) navigator.clipboard.writeText(result.content);
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-3">
        <div>
          <label className={labelCls}>문서 유형</label>
          <select value={docType} onChange={e => setDocType(e.target.value)} className={selectCls}>
            {DOC_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>언어</label>
          <select value={lang} onChange={e => setLang(e.target.value)} className={selectCls}>
            <option value="ko">한국어</option>
            <option value="en">English</option>
            <option value="ja">日本語</option>
          </select>
        </div>
      </div>

      <textarea
        value={prompt}
        onChange={e => setPrompt(e.target.value)}
        placeholder={`작성할 문서에 대해 설명해주세요...\n\n예시:\n・ 거래처에 납품 일정 변경 안내 이메일\n・ 신규 복합기 렌탈 서비스 제안서\n・ 사무실 이전 공지문\n・ 월간 매출 보고서 양식`}
        rows={5}
        className={inputCls + " resize-y"}
      />

      <button onClick={run} disabled={loading || !prompt.trim()} className={`${btn} bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700`}>
        {loading ? "작성 중..." : "문서 생성하기"}
      </button>

      {loading && <ThinkingStep loading={true} steps={['문서 유형 분석 중...', '구조 설계 중...', '초안 작성 중...', '검토 및 다듬기 중...']} />}

      {result && !result.error && (
        <div className={card + " space-y-4"}>
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-gray-900">{result.title}</h3>
            <button onClick={copyToClipboard} className="text-xs text-blue-500 hover:text-blue-700 font-medium flex items-center gap-1">{IC.copy} 복사</button>
          </div>
          <div className="bg-gray-50 rounded-xl p-5 prose prose-sm max-w-none text-gray-800">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{result.content}</ReactMarkdown>
          </div>
          {result.tips?.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <h4 className="text-xs font-semibold text-blue-700 mb-2 flex items-center gap-1.5">{IC.bulb} 활용 팁</h4>
              <ul className="space-y-1">
                {result.tips.map((t, i) => <li key={i} className="text-xs text-blue-800">• {t}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
      {result?.error && <p className="text-red-500 text-sm">{result.error}</p>}
    </div>
  );
}

/* ── Image Analysis Tab ──────────────────────────────── */
function ImageTab() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mode, setMode] = useState("general");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef();

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setResult(null);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const run = async () => {
    if (!file) return;
    setLoading(true);
    setResult(null);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("mode", mode);
    try {
      const { data } = await api.post("/api/ai-tools/analyze-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
    } catch (e) {
      setResult({ error: e.response?.data?.error || "오류 발생" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label className={labelCls}>분석 모드</label>
          <select value={mode} onChange={e => setMode(e.target.value)} className={selectCls}>
            <option value="general">종합 분석</option>
            <option value="ocr">텍스트 추출 (OCR)</option>
            <option value="print_quality">인쇄 품질 진단</option>
          </select>
        </div>
      </div>

      <div
        onClick={() => fileRef.current?.click()}
        className="border-2 border-dashed border-gray-300 rounded-2xl p-8 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-all"
      >
        {preview ? (
          <img src={preview} alt="preview" className="max-h-64 mx-auto rounded-xl" />
        ) : (
          <div className="text-gray-400">
            <div className="text-gray-300 mb-2">{sv("M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2zM12 13a4 4 0 100-8 4 4 0 000 8z", 40)}</div>
            <p className="text-sm">클릭하여 이미지를 선택하세요</p>
            <p className="text-xs text-gray-300 mt-1">JPG, PNG, WebP (최대 10MB)</p>
          </div>
        )}
        <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
      </div>

      <button onClick={run} disabled={loading || !file} className={`${btn} bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600`}>
        {loading ? "분석 중..." : "이미지 분석하기"}
      </button>

      {loading && <ThinkingStep loading={true} steps={['이미지 인식 중...', '객체 및 텍스트 감지 중...', 'AI 분석 결과 생성 중...']} />}

      {result && !result.error && (
        <div className={card + " space-y-4"}>
          {/* General mode */}
          {result.description && (
            <div>
              <h4 className="text-sm font-semibold text-gray-800 mb-1 flex items-center gap-1.5">{IC.camera} 이미지 설명</h4>
              <p className="text-sm text-gray-700">{result.description}</p>
            </div>
          )}
          {result.document_type && (
            <span className="inline-block px-3 py-1 rounded-full bg-indigo-50 text-indigo-600 text-xs font-semibold">{result.document_type}</span>
          )}
          {result.objects?.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-800 mb-1 flex items-center gap-1.5">{IC.search} 감지된 요소</h4>
              <div className="flex flex-wrap gap-1.5">
                {result.objects.map((o, i) => (
                  <span key={i} className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs">{o}</span>
                ))}
              </div>
            </div>
          )}
          {result.text_content && (
            <div>
              <h4 className="text-sm font-semibold text-gray-800 mb-1 flex items-center gap-1.5">{IC.pen} 추출된 텍스트</h4>
              <pre className="bg-gray-50 rounded-xl p-4 text-xs text-gray-700 overflow-auto whitespace-pre-wrap max-h-60">{result.text_content}</pre>
            </div>
          )}
          {/* OCR structured data */}
          {result.structured_data && Object.keys(result.structured_data).length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-800 mb-1 flex items-center gap-1.5">{IC.chart} 구조화 데이터</h4>
              <div className="bg-blue-50 rounded-xl p-4 text-xs space-y-1">
                {Object.entries(result.structured_data).map(([k, v]) => (
                  <div key={k} className="flex gap-2">
                    <span className="font-semibold text-blue-700 min-w-[80px]">{k}:</span>
                    <span className="text-blue-900">{typeof v === "object" ? JSON.stringify(v, null, 2) : String(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Print quality */}
          {result.quality_score && (
            <div>
              <h4 className="text-sm font-semibold text-gray-800 mb-1 flex items-center gap-1.5">{IC.target} 인쇄 품질 점수</h4>
              <div className="flex items-center gap-3">
                <div className={`text-3xl font-bold ${result.quality_score >= 7 ? 'text-green-500' : result.quality_score >= 4 ? 'text-yellow-500' : 'text-red-500'}`}>
                  {result.quality_score}/10
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className={`h-3 rounded-full ${result.quality_score >= 7 ? 'bg-green-500' : result.quality_score >= 4 ? 'bg-yellow-500' : 'bg-red-500'}`}
                    style={{ width: `${result.quality_score * 10}%` }}
                  />
                </div>
              </div>
            </div>
          )}
          {result.diagnosis && (
            <div>
              <h4 className="text-sm font-semibold text-gray-800 mb-1 flex items-center gap-1.5">{IC.scope} 종합 진단</h4>
              <p className="text-sm text-gray-700">{result.diagnosis}</p>
            </div>
          )}
          {result.issues?.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-red-600 mb-1 flex items-center gap-1.5">{IC.warn} 발견된 문제</h4>
              <ul className="space-y-1">{result.issues.map((s, i) => <li key={i} className="text-sm text-red-700">• {s}</li>)}</ul>
            </div>
          )}
          {result.solutions?.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-green-600 mb-1 flex items-center gap-1.5">{IC.check} 해결 방법</h4>
              <ul className="space-y-1">{result.solutions.map((s, i) => <li key={i} className="text-sm text-green-700">• {s}</li>)}</ul>
            </div>
          )}
          {result.copier_recommendation && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <h4 className="text-xs font-semibold text-blue-700 mb-1 flex items-center gap-1.5">{IC.printer} 복합기 추천</h4>
              <p className="text-sm text-blue-900">{result.copier_recommendation}</p>
            </div>
          )}
          {result.colors?.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-800 mb-1 flex items-center gap-1.5">{IC.palette} 주요 색상</h4>
              <div className="flex gap-2">
                {result.colors.map((c, i) => <span key={i} className="px-2 py-0.5 rounded-full bg-gray-100 text-xs text-gray-600">{c}</span>)}
              </div>
            </div>
          )}
          {result.suggestions?.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <h4 className="text-xs font-semibold text-green-700 mb-1 flex items-center gap-1.5">{IC.bulb} 활용 제안</h4>
              <ul className="space-y-1">{result.suggestions.map((s, i) => <li key={i} className="text-xs text-green-800">• {s}</li>)}</ul>
            </div>
          )}
        </div>
      )}
      {result?.error && <p className="text-red-500 text-sm">{result.error}</p>}
    </div>
  );
}

/* ── Print Cost Optimizer Tab ────────────────────────── */
function OptimizerTab() {
  const [form, setForm] = useState({
    monthly_bw: "", monthly_color: "", employees: "", current_cost: "",
    paper_size: "A4", priorities: ["비용절감"],
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const PRIORITIES = ["비용절감", "고속출력", "컬러품질", "내구성", "소형/공간절약", "친환경"];

  const togglePriority = (p) => {
    setForm(prev => ({
      ...prev,
      priorities: prev.priorities.includes(p)
        ? prev.priorities.filter(x => x !== p)
        : [...prev.priorities, p],
    }));
  };

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/api/ai-tools/print-optimizer", {
        ...form,
        monthly_bw: Number(form.monthly_bw) || 0,
        monthly_color: Number(form.monthly_color) || 0,
        employees: Number(form.employees) || 1,
        current_cost: Number(form.current_cost) || 0,
      });
      setResult(data);
    } catch (e) {
      setResult({ error: e.response?.data?.error || "오류 발생" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <label className={labelCls}>월 흑백 출력량</label>
          <input type="number" value={form.monthly_bw} onChange={e => setForm(p => ({ ...p, monthly_bw: e.target.value }))} placeholder="매" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>월 컬러 출력량</label>
          <input type="number" value={form.monthly_color} onChange={e => setForm(p => ({ ...p, monthly_color: e.target.value }))} placeholder="매" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>직원 수</label>
          <input type="number" value={form.employees} onChange={e => setForm(p => ({ ...p, employees: e.target.value }))} placeholder="명" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>현재 월 인쇄비</label>
          <input type="number" value={form.current_cost} onChange={e => setForm(p => ({ ...p, current_cost: e.target.value }))} placeholder="원" className={inputCls} />
        </div>
      </div>

      <div>
        <label className={labelCls}>필요 용지 크기</label>
        <div className="flex gap-2">
          {["A4", "A3"].map(s => (
            <button key={s} onClick={() => setForm(p => ({ ...p, paper_size: s }))}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition ${form.paper_size === s ? "bg-blue-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>{s}</button>
          ))}
        </div>
      </div>

      <div>
        <label className={labelCls}>우선순위 (복수 선택)</label>
        <div className="flex flex-wrap gap-2">
          {PRIORITIES.map(p => (
            <button key={p} onClick={() => togglePriority(p)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${form.priorities.includes(p) ? "bg-blue-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>{p}</button>
          ))}
        </div>
      </div>

      <button onClick={run} disabled={loading} className={`${btn} bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700`}>
        {loading ? "분석 중..." : "비용 최적화 분석"}
      </button>

      {loading && <ThinkingStep loading={true} steps={['비용 데이터 수집 중...', '사용 패턴 분석 중...', '최적화 방안 수립 중...', '절감 예상액 계산 중...']} />}

      {result && !result.error && (
        <div className="space-y-5">
          {/* Cost analysis */}
          {result.analysis && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: "현재 추정 비용", value: result.analysis.current_monthly_estimate, color: "text-gray-700" },
                { label: "최적화 후 비용", value: result.analysis.optimized_monthly_estimate, color: "text-blue-600" },
                { label: "연간 절감액", value: result.analysis.annual_savings, color: "text-green-600" },
                { label: "절감률", value: result.analysis.saving_rate, color: "text-emerald-600" },
              ].map((item, i) => (
                <div key={i} className="bg-gray-50 rounded-xl p-4 text-center">
                  <div className="text-xs text-gray-500 mb-1">{item.label}</div>
                  <div className={`text-lg font-bold ${item.color}`}>{item.value}</div>
                </div>
              ))}
            </div>
          )}

          {/* Recommendation */}
          {result.recommendation && (
            <div className={card + " border-blue-200 bg-blue-50/50"}>
              <h4 className="text-sm font-semibold text-blue-800 mb-2 flex items-center gap-1.5">{IC.target} 추천: {result.recommendation.type === "rental" ? "렌탈" : "구매"}</h4>
              <p className="text-sm text-gray-700 mb-3">{result.recommendation.reason}</p>
              {result.recommendation.plan_details && (
                <div className="bg-white rounded-xl p-4 text-sm text-gray-700">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{result.recommendation.plan_details}</ReactMarkdown>
                </div>
              )}
            </div>
          )}

          {/* Recommended copiers */}
          {result.recommended_copiers?.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-1.5">{IC.grid} 추천 복합기</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {result.recommended_copiers.map(c => (
                  <div key={c.id} className={card + " flex gap-4"}>
                    {c.image_url && <img src={c.image_url} alt={c.model_name} className="w-20 h-20 object-contain rounded-lg bg-gray-50" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-gray-500">{c.brand_name}</div>
                      <div className="text-sm font-semibold text-gray-900 truncate">{c.model_name}</div>
                      <div className="text-xs text-gray-500 mt-1">{c.color_type === "color" ? "컬러" : "흑백"} · {c.speed_ppm}ppm</div>
                      <div className="text-xs text-gray-500">{c.price_range}</div>
                      <div className="flex gap-2 mt-2">
                        {c.mall_url && <a href={c.mall_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline flex items-center gap-1">{IC.bag} 구매</a>}
                        {c.rental_url && <a href={c.rental_url} target="_blank" rel="noopener noreferrer" className="text-xs text-green-600 hover:underline flex items-center gap-1">{IC.clip} 렌탈</a>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Comparison */}
          {result.comparison?.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-1.5">{IC.scale} 옵션 비교</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {result.comparison.map((opt, i) => (
                  <div key={i} className={card}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-gray-900">{opt.option}</span>
                      <span className="text-sm font-bold text-blue-600">{opt.monthly_cost}</span>
                    </div>
                    {opt.pros?.length > 0 && (
                      <div className="mb-1">{opt.pros.map((p, j) => <span key={j} className="text-xs text-green-600 mr-2 inline-flex items-center gap-0.5">{IC.check} {p}</span>)}</div>
                    )}
                    {opt.cons?.length > 0 && (
                      <div>{opt.cons.map((c, j) => <span key={j} className="text-xs text-red-500 mr-2 inline-flex items-center gap-0.5">{IC.warn} {c}</span>)}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tips */}
          {result.tips?.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <h4 className="text-xs font-semibold text-green-700 mb-2 flex items-center gap-1.5">{IC.bulb} 비용 절감 팁</h4>
              <ul className="space-y-1">{result.tips.map((t, i) => <li key={i} className="text-xs text-green-800">• {t}</li>)}</ul>
            </div>
          )}
        </div>
      )}
      {result?.error && <p className="text-red-500 text-sm">{result.error}</p>}
    </div>
  );
}

/* ── General AI Ask Tab ──────────────────────────────── */
function AskTab() {
  const [question, setQuestion] = useState("");
  const [context, setContext] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);

  const run = async () => {
    if (!question.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/api/ai-tools/ask", { question, context });
      setResult(data);
      setHistory(prev => [{ q: question, a: data.answer }, ...prev].slice(0, 10));
      setQuestion("");
    } catch (e) {
      setResult({ error: e.response?.data?.error || "오류 발생" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      <textarea
        value={question}
        onChange={e => setQuestion(e.target.value)}
        placeholder="무엇이든 질문하세요...\n\n예시:\n・ 이 계약서 조항의 법적 의미가 뭐야?\n・ Python으로 PDF를 이미지로 변환하는 코드\n・ 마케팅 전략 3가지 추천해줘"
        rows={4}
        className={inputCls + " resize-y"}
        onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); run(); } }}
      />
      <details className="text-xs text-gray-400">
        <summary className="cursor-pointer hover:text-gray-600">+ 참고 자료 추가 (선택)</summary>
        <textarea
          value={context}
          onChange={e => setContext(e.target.value)}
          placeholder="AI가 참고할 추가 맥락 정보를 넣으세요..."
          rows={3}
          className={inputCls + " mt-2 resize-y"}
        />
      </details>
      <button onClick={run} disabled={loading || !question.trim()} className={`${btn} bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700`}>
        {loading ? "생각 중..." : "질문하기"}
      </button>

      {loading && <ThinkingStep loading={true} steps={['질문 내용 분석 중...', '관련 지식 검색 중...', '답변 생성 중...']} />}

      {result && !result.error && (
        <div className={card}>
          <div className="prose prose-sm max-w-none text-gray-800">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{result.answer}</ReactMarkdown>
          </div>
        </div>
      )}
      {result?.error && <p className="text-red-500 text-sm">{result.error}</p>}

      {history.length > 0 && (
        <div className="space-y-3 mt-6">
          <h4 className="text-xs font-semibold text-gray-400">이전 질문</h4>
          {history.map((h, i) => (
            <details key={i} className="bg-gray-50 rounded-xl p-3">
              <summary className="text-sm text-gray-700 cursor-pointer font-medium">{h.q}</summary>
              <div className="mt-2 prose prose-sm max-w-none text-gray-600 border-t border-gray-200 pt-2">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{h.a}</ReactMarkdown>
              </div>
            </details>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Main Page ───────────────────────────────────────── */
export default function AiToolsPage() {
  const [tab, setTab] = useState("summarize");
  const { user } = useAuth();

  const TAB_COMPONENTS = {
    summarize: SummarizeTab,
    translate: TranslateTab,
    write: WriteTab,
    image: ImageTab,
    optimizer: OptimizerTab,
    ask: AskTab,
  };

  const ActiveTab = TAB_COMPONENTS[tab];

  /* ── Login gate ─────────────────────────────────────── */
  if (!user) {
    return (
      <div className="dept-access-page">
        <SEO title="AI 도구 | 일광오피스" description="로그인 후 AI 도구를 무료로 사용하세요" path="/ai-tools" />
        <div className="dept-access-page__inner">
          <div className="dept-access-page__mark" aria-hidden="true">AI</div>
          <div className="dept-access-page__copy">
            <span className="dept-access-page__eyebrow">일광오피스 업무 지원</span>
            <h1>AI 도구</h1>
            <p>문서 요약, 번역, 이미지 분석 도구는 로그인 후 이용할 수 있습니다.</p>
            <div className="dept-access-page__actions">
              <Link to="/login" className="dept-access-page__primary">
                로그인
              </Link>
              <Link to="/signup" className="dept-access-page__secondary">
                회원가입
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen">
      <SEO
        title="AI 도구 | 일광오피스"
        description="AI 문서 요약, 번역, 비즈니스 문서 작성, 이미지 분석, 인쇄 비용 최적화 등 무료 AI 도구"
        keywords="AI 도구, 문서 요약, AI 번역, 비즈니스 문서, OCR, 인쇄 비용, 복합기 추천"
        path="/ai-tools"
      />

      {/* Hero */}
      <section className="pt-12 pb-6">
        <div className="max-w-[980px] mx-auto px-4">
          <h1 className="text-[32px] md:text-[44px] font-semibold tracking-tight text-apple-black leading-tight">
            AI 도구
          </h1>
          <p className="mt-1 text-[15px] text-apple-gray">
            업무 효율을 높이는 AI 유틸리티 모음
          </p>
        </div>
      </section>

      {/* Tab bar */}
      <section className="border-b border-gray-200 sticky top-12 bg-white/95 backdrop-blur-xl z-30">
        <div className="max-w-[980px] mx-auto px-4">
          <div className="flex gap-1 overflow-x-auto py-2 scrollbar-hide">
            {TABS.map(t => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`whitespace-nowrap px-4 py-2 rounded-xl text-xs font-medium transition-all ${
                  tab === t.id
                    ? "bg-[#0071e3] text-white shadow-sm"
                    : "text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                }`}
              >
                <span className="inline-flex items-center gap-1.5">{t.icon} {t.label}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-8">
        <div className="max-w-[980px] mx-auto px-4">
          {/* Tab description */}
          <div className="mb-6">
            <p className="text-sm text-gray-500">
              {TABS.find(t => t.id === tab)?.desc}
            </p>
          </div>
          <ActiveTab />
        </div>
      </section>
    </div>
  );
}
